export const VIEWPORT_WHEEL_DISTANCE_SCROLL_MARKER = 'KAIROS_VIEWPORT_WHEEL_DISTANCE_SCROLL';
export const VIEWPORT_WHEEL_CINE_STEP_CAP_MARKER = 'KAIROS_VIEWPORT_WHEEL_CINE_STEP_CAP';
export const VIEWPORT_WHEEL_BURST_DAMPING_MARKER = 'KAIROS_VIEWPORT_WHEEL_BURST_DAMPING';

export const VIEWPORT_WHEEL_PIXELS_PER_SLICE = 120;
// Only damp high-frequency wheel/trackpad bursts after browser delivery.
// The headed 12-burst contract showed browser/compositor jitter can stretch a
// nominal 80-83 ms singleton delivery to 114 ms. Keep that bounded jitter in
// the same settled burst while leaving deliveries beyond 125 ms undamped.
export const VIEWPORT_FAST_WHEEL_BURST_MS = 125;
export const VIEWPORT_FAST_WHEEL_DAMPING = 0.6;
export const VIEWPORT_WHEEL_LINE_HEIGHT_PX = 40;
export const VIEWPORT_WHEEL_PAGE_HEIGHT_PX = 800;

export const normalizeViewportWheelDelta = (event) => {
  const mode = event?.deltaMode ?? 0;
  const unit = mode === 1 ? VIEWPORT_WHEEL_LINE_HEIGHT_PX : (mode === 2 ? VIEWPORT_WHEEL_PAGE_HEIGHT_PX : 1);
  return (event?.deltaY || 0) * unit;
};

export const applyViewportWheelBurstDamping = (deltaPixels, previousEventAt, now) => {
  const delta = Number(deltaPixels) || 0;
  const previous = Number(previousEventAt);
  const current = Number(now);
  if (!delta || !Number.isFinite(previous) || previous <= 0 || !Number.isFinite(current)) return delta;
  const dt = current - previous;
  if (dt >= 0 && dt <= VIEWPORT_FAST_WHEEL_BURST_MS) {
    if (Math.abs(delta) <= VIEWPORT_WHEEL_PIXELS_PER_SLICE) return delta;
    return delta * VIEWPORT_FAST_WHEEL_DAMPING;
  }
  return delta;
};

export const getViewportWheelMaxStepsPerFrame = (seriesSize) => {
  const size = Number(seriesSize) || 0;
  // Keep visible wheel motion cine-like under browser/network pressure. A fast
  // wheel/trackpad burst may accumulate many slice steps before the next rAF;
  // applying all of them at once makes CT stacks jump instead of scroll. Drop
  // above-cap backlog instead of draining it later so scroll never feels queued.
  if (size > 800) return 16;
  if (size > 400) return 10;
  if (size > 200) return 5;
  if (size > 80) return 3;
  return 2;
};

export const getViewportWheelStepBudget = (seriesSize, rawSteps) => {
  const size = Math.max(0, Number(seriesSize) || 0);
  const ordinaryBudget = getViewportWheelMaxStepsPerFrame(size);
  const magnitude = Math.max(0, Math.floor(Math.abs(Number(rawSteps) || 0)));
  // Browsers coalesce a deliberate high-speed wheel/trackpad sweep into a
  // smaller number of large trusted deltas. Preserve that distance intent so
  // a radiologist can cross a stack in one gesture, while ordinary detents and
  // moderate flings retain the cine-like per-frame cap above.
  const coalescedThreshold = Math.max(ordinaryBudget + 1, Math.ceil(size / 32));
  if (magnitude <= coalescedThreshold) return ordinaryBudget;
  // Budget for up to two intended fast-sweep samples coalesced into one
  // animation frame. This preserves summed trusted distance without allowing
  // one frame to traverse more than one eighth of a large stack.
  return Math.max(ordinaryBudget, Math.ceil(size / 8));
};

export const consumeViewportWheelSteps = (accumulatedPixels, seriesSize, { rapidBurst } = {}) => {
  const pixels = Number(accumulatedPixels) || 0;
  const sign = pixels < 0 ? -1 : 1;
  const rawSteps = Math.floor(Math.abs(pixels) / VIEWPORT_WHEEL_PIXELS_PER_SLICE);
  if (rawSteps <= 0) return { steps: 0, remainder: pixels };
  const size = Math.max(0, Number(seriesSize) || 0);
  const ordinaryBudget = getViewportWheelMaxStepsPerFrame(size);
  const coalescedThreshold = Math.max(ordinaryBudget + 1, Math.ceil(size / 32));
  if (rapidBurst === false && rawSteps > coalescedThreshold) return { steps: sign, remainder: 0 };
  const maxSteps = getViewportWheelStepBudget(size, rawSteps);
  const cappedRawSteps = Math.min(rawSteps, maxSteps);
  const steps = sign * cappedRawSteps;
  return {
    steps,
    remainder: rawSteps > maxSteps ? 0 : pixels - (steps * VIEWPORT_WHEEL_PIXELS_PER_SLICE),
  };
};
